import type { ApolloError } from "@apollo/client";
import { produce } from "immer";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import type { Location } from "react-router";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { storage } from "@/app-utils/proxy-storage.js";
import { pushRoute } from "@/app-utils/route.ts";
import { PubSubActions } from "@/global/pubsub-actions.js";
import type {
  InputUserAccountInput,
  InputUserParamsInput,
  InputUserPasswordChangeRequestInput,
  User,
  UserParams,
  View,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { CLASSIC_UI_TOKEN_KEY, EXTERNAL_LOGIN_TOKEN, LOG_ACTION, TOKEN_KEY, VIEW } from "@/settings/settings.ts";
import { client, webSocketClient } from "@/tools.js";

import { notify } from "../../modules/notifications/notify.js";
import type { IUserAccount } from "../../ui-modules/core/CreateAccountPasswordPage.js";
import {
  AUTHENTICATION_SUCCESS,
  LOAD_USER_CAPABILITIES_FAILURE,
  LOAD_USER_CAPABILITIES_REQUEST,
  LOAD_USER_CAPABILITIES_SUCCESS,
  LOAD_USER_PARAMS_FAILURE,
  LOAD_USER_PARAMS_SUCCESS,
  LOAD_VIEW_SUCCESS,
  LOGIN_USER_FAILURE,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER,
  SET_REFERENCE_GENOME,
  UPDATE_LOCAL_USER_PREF,
  UPDATE_SHOW_INTRO,
  UPDATE_TIPS_ARE_OFF,
  UPDATE_USER_PARAMS_FAILURE,
  UPDATE_USER_PARAMS_REQUEST,
  UPDATE_USER_PARAMS_SUCCESS,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { loadConfiguration } from "./config.js";
import { loadSequenceFeatureTypes, loadSequenceTypes } from "./listing.js";

export interface IAuditLogPayload {
  type: number;
  message: string;
  payload: string | object;
}

export interface LocalUserPref {
  showShortcutsHelp: boolean;
}

/**
 * State definition
 */
export interface IAuthState {
  token: string;
  session: User;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  statusText: string;
  firstLogin: boolean;
  showTipsAreOff: boolean;
  localUserPref: LocalUserPref;
}

export const initialState: IAuthState = {
  token: null,
  session: null,
  isAuthenticated: false,
  isAuthenticating: false,
  statusText: null,
  firstLogin: false,
  showTipsAreOff: false,
  localUserPref: {
    showShortcutsHelp: false,
  },
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_VIEW_SUCCESS]: (state: IAuthState, view: View): IAuthState => {
    const viewRefGenomeId: number = getRefGenomeFromSavedView(view);
    if (viewRefGenomeId) {
      return produce(state, (draft) => {
        draft.session.params.referenceGenome = viewRefGenomeId;
      });
    }
    return state;
  },
  [LOGIN_USER_REQUEST]: (state: IAuthState): IAuthState => {
    return {
      ...state,
      isAuthenticating: true,
      statusText: null,
    };
  },
  [AUTHENTICATION_SUCCESS]: (state: IAuthState, payload): IAuthState => {
    return {
      ...state,
      isAuthenticated: true,
      token: payload.token,
    };
  },
  [LOGIN_USER_SUCCESS]: (state: IAuthState, payload: User): IAuthState => {
    return {
      ...state,
      isAuthenticating: false,
      isAuthenticated: true,
      session: payload,
      statusText: "You have been successfully logged in.",
      firstLogin: payload.params.firstLogin,
    };
  },
  [LOGIN_USER_FAILURE]: (state: IAuthState, payload): IAuthState => {
    return {
      ...state,
      isAuthenticating: false,
      isAuthenticated: false,
      session: null,
      statusText: payload?.statusText,
    };
  },
  [LOAD_USER_CAPABILITIES_SUCCESS]: (state: IAuthState, payload: User): IAuthState => {
    return {
      ...state,
      session: {
        ...state.session,
        capabilities: payload.capabilities,
      },
    };
  },
  [LOAD_USER_PARAMS_SUCCESS]: (state: IAuthState, params: UserParams): IAuthState => {
    return produce(state, (draft) => {
      draft.session.params = params;
    });
  },
  [UPDATE_USER_PARAMS_REQUEST]: (state: IAuthState, params: UserParams): IAuthState => {
    return produce(state, (draft) => {
      draft.session.params = { ...draft.session.params, ...params };
    });
  },
  [UPDATE_LOCAL_USER_PREF]: (state: IAuthState, prefs: LocalUserPref): IAuthState => {
    return {
      ...state,
      localUserPref: { ...state.localUserPref, ...prefs },
    };
  },
  [UPDATE_TIPS_ARE_OFF]: (state: IAuthState, payload: boolean): IAuthState => {
    return {
      ...state,
      showTipsAreOff: payload,
    };
  },
  [UPDATE_SHOW_INTRO]: (state: IAuthState, payload: boolean): IAuthState => {
    return produce(state, (draft) => {
      draft.session.params!.firstLogin = payload;
    });
  },
});

export function loginUserToken(token, redirect = "/"): ReduxAction<Promise<User>> {
  return (dispatch, _, webApiClient) => {
    dispatch(loginUserRequest());
    return webApiClient
      .fetchAuthTokenEx(token)
      .then((authToken) => {
        try {
          jwtDecode(authToken.token); // check the validity of token
        } catch {
          // this will be caught by the Promise catch, like a reject case
          throw new Error("Authentication failed : token is invalid");
        }

        storage[TOKEN_KEY] = authToken.token;
        setAuthCookie(authToken.token);
        webSocketClient.connectAndKeepReconnecting();

        // if logging in after password change, redirect to home page
        if (redirect === "/user/changePassword") {
          redirect = "/";
        }
        return dispatch(authenticatedAndLoadUserSession(authToken.token, redirect));
      })
      .catch((error) => {
        delete storage[TOKEN_KEY];
        removeAuthCookie();
        createErrorHandler(dispatch, {
          actionType: LOGIN_USER_FAILURE,
          actionPayload: { statusText: error.message },
          showAlert: false,
          throwError: true, // so that the error is caught in the login form and allows to react accordingly
        })(error);
        return null;
      });
  };
}
export function loginUser(username: string, password: string, redirect = "/"): ReduxAction<Promise<User | null>> {
  return (dispatch, _, webApiClient) => {
    dispatch(loginUserRequest());

    return webApiClient
      .fetchAuthToken(username, password)
      .then((authToken) => {
        try {
          jwtDecode(authToken.token); // check the validity of token
        } catch {
          // this will be caught by the Promise catch, like a reject case
          throw new Error("Authentication failed : token is invalid");
        }

        storage[TOKEN_KEY] = authToken.token;
        setAuthCookie(authToken.token);

        webSocketClient.connectAndKeepReconnecting();

        // if logging in after password change, redirect to home page
        if (redirect === "/user/changePassword") {
          redirect = "/";
        }

        return dispatch(authenticatedAndLoadUserSession(authToken.token, redirect));
      })
      .catch((error: ApolloError) => {
        delete storage[TOKEN_KEY];
        removeAuthCookie();
        void createErrorHandler(dispatch, {
          actionType: LOGIN_USER_FAILURE,
          actionPayload: { statusText: error.message },
          showAlert: false,
          throwError: true, // so that the error is caught in the login form and allows to react accordingly
        })(error);
        return null;
      });
  };
}

export function setAuthCookie(token) {
  const d = new Date();
  d.setTime(d.getTime() + 8 * 60 * 60 * 1000); // 8 hours expiry
  const expires = "expires=" + d.toUTCString();
  // "SameSite" flag makes this cookie only be sent when request in same domain and subdomain
  // "Secure" flag should be added when we support https only
  document.cookie = `${CLASSIC_UI_TOKEN_KEY}=${token}; ${expires}; path=/; SameSite;`;
}

export function eraseCookie(name) {
  Cookies.remove(name);
}

function removeAuthCookie() {
  eraseCookie(EXTERNAL_LOGIN_TOKEN);
  eraseCookie(CLASSIC_UI_TOKEN_KEY);
}

/**
 * if the token is already available we still need to fetch the user session.
 * If we set the auth state after we have loaded the user session then the dispatch of the action
 * will happen after the routes' "requireAuthentication" - which redirects to login page in
 * case auth.isAuthenticated is still false - because we have to process the response of a promise.
 * Therefore we do set the auth state with the token and isAuthenticated properties BEFORE we load the session.
 * That means that for a short moment the page will be loaded without the user session data.
 * It might have some implication if the user session provides the authorization data in the future, but
 * we would be able to assume the user has no permission at all until the session is fetched completely.
 */
export function authenticatedAndLoadUserSession(token: string, redirect?: string): ReduxAction<Promise<User>> {
  return (dispatch) => {
    dispatch(authenticationSuccess(token));
    dispatch(loadConfiguration());
    dispatch(loadSequenceFeatureTypes());
    dispatch(loadSequenceTypes());
    return dispatch(loadUserSession(redirect));
  };
}

export function loadUserSession(redirect?: string): ReduxAction<Promise<User>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient
      .fetchCurrentUserData()
      .then((user) => {
        dispatch(loginUserSuccess(user));
        dispatch(auditUserLogin());
        dispatch(redirectTo(redirect));
        return user;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOGIN_USER_FAILURE,
          showAlert: false,
        }),
      );
  };
}

export function loadUserCapabilities(): ReduxAction<Promise<User>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({ type: LOAD_USER_CAPABILITIES_REQUEST });
    return webApiClient
      .fetchCurrentUserCapabilities()
      .then((user) => {
        dispatch({ type: LOAD_USER_CAPABILITIES_SUCCESS, payload: user });
        return user;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_USER_CAPABILITIES_FAILURE,
          showAlert: false,
        }),
      );
  };
}

function authenticationSuccess(token: string) {
  // const type = AUTHENTICATION_SUCCESS;
  return { type: AUTHENTICATION_SUCCESS, payload: { token } };
}

function loginUserSuccess(payload: User) {
  return { type: LOGIN_USER_SUCCESS, payload };
}

// set isAuthenticated flag of the user back to false and additional cleanup, and
// will let the RequireAuthentication component take care of the redirect logic
export function unauth_logout(): ReduxAction<void> {
  return (dispatch, getState: () => IAppState, _webApiClient) => {
    // make sure the logout procedure does happen only once
    if (!getState().auth.isAuthenticated) {
      return null;
    }
    dispatch(logoutInternal());
    dispatch(deleteTokenAndLogout());
  };
}

// meant for explicit logout, with redirect to /login page
export function logout(): ReduxAction<Promise<void>> {
  return (dispatch, _getState, _webApiClient) => {
    dispatch(logoutInternal());
    return dispatch(logoutAndRedirect());
  };
}

function logoutAndRedirect(): ReduxAction<Promise<void>> {
  return (dispatch, getState, webApiClient) => {
    const state = getState();
    const external = state.config?.configuration?.application?.externalAuthentication;
    dispatch(deleteTokenAndLogout());
    return webApiClient.logout().then(
      () => {
        dispatch(returnToLoginPage(external));
      },
      (_error) => {
        // If the logout WebApi call fails we perform the same actions to clear the broken token
        // from the storage and redirect the user to the login page.
        dispatch(returnToLoginPage(external));
      },
    );
  };
}

export function changeYourPassword(payload: { newPassword: string; oldPassword: string }): ReduxAction<Promise<void>> {
  return (dispatch, getState, webApiClient) => {
    const user: InputUserAccountInput = { ...getState().auth.session };

    const request: InputUserPasswordChangeRequestInput = {
      userName: user.username,
      newPassword: payload.newPassword,
      oldPassword: payload.oldPassword,
    };

    return webApiClient
      .changeYourPassword(request)
      .then(() => {
        notify.info({
          title: selMessage({ id: "auth.password.modified.successTitle" }),
          children: selMessage({ id: "auth.password.modified.successMessage" }),
        });
      })
      .then(() => pushRoute("/logout"));
  };
}

export function generateRestToken(lifetime: number): ReduxAction<Promise<string>> {
  return (_dispatch, _getState, webApiClient) => {
    return webApiClient.generateRestToken(lifetime).then((res) => {
      return res;
    });
  };
}

export function setPassword(payload: any): ReduxAction<Promise<void>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient
      .setPassword(payload)
      .then(() => {
        notify.info({
          title: selMessage({ id: "auth.password.set.successTitle" }),
          children: selMessage({ id: "auth.password.set.successMessage" }),
        });
      })
      .then(() => dispatch(returnToLoginPage(false)));
  };
}

export function canResetPassword(token: string): ReduxAction<Promise<IUserAccount>> {
  return (_dispatch, _getState, webApiClient) => {
    return webApiClient.isChangePasswordTokenValid(token);
  };
}

export function logoutInternal(): ReduxAction<Promise<boolean>> {
  return (_dispatch, _getState, webApiClient) => {
    return webApiClient.logout();
  };
}

function logoutExternal(): ReduxAction<void> {
  return (_dispatch, _getState, webApiClient) => {
    webApiClient.logoutFromExternal("home");
  };
}

function returnToLoginPage(external): ReduxAction<void> {
  return (dispatch) => {
    dispatch(deleteTokenAndLogout());
    if (external) {
      dispatch(logoutExternal());
    } else {
      pushRoute("/login");
    }
    webSocketClient.closeAndNoReconnecting();
  };
}

export function redirectTo(redirect: string) {
  return (dispatch, _getState, _webApiClient) => {
    if (redirect) {
      if (redirect?.startsWith("http")) {
        // redirect for Classic UI authentication
        window.location.assign(redirect);
        pushRoute("/inprogress"); // should return a ReduxAction that has no impact on the location change
      } else {
        pushRoute(redirect);
      }
    }
  };
}

function loginUserRequest() {
  return { type: LOGIN_USER_REQUEST };
}

export function deleteTokenAndLogout(): ReduxAction<void> {
  return (dispatch) => {
    void client?.().cache?.reset();
    PubSub.publish(PubSubActions.LOGOUT);
    removeAuthCookie();
    delete storage[TOKEN_KEY];
    dispatch({ type: LOGOUT_USER });
  };
}

export function loadUserParams(): ReduxAction<Promise<UserParams>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient
      .fetchUserParams()
      .then((response: UserParams) => {
        dispatch(loadUserParamsSuccess(response));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_USER_PARAMS_FAILURE,
          alertTitle: "Fetching of User Configuration Parameters Failed",
        }),
      );
  };
}

function loadUserParamsSuccess(payload: UserParams) {
  return { type: LOAD_USER_PARAMS_SUCCESS, payload };
}

export function updateUserParams(userParams: InputUserParamsInput): ReduxAction<Promise<UserParams>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({
      type: UPDATE_USER_PARAMS_REQUEST,
      payload: userParams,
    });

    return webApiClient
      .saveUserParams(userParams)
      .then((payload) => dispatch({ type: UPDATE_USER_PARAMS_SUCCESS, payload }))
      .catch(
        createErrorHandler(dispatch, {
          actionType: UPDATE_USER_PARAMS_FAILURE,
          alertTitle: "Updating User Configuration Parameters Failed",
          alertMessage: "Could not update client configuration",
        }),
      );
  };
}

export function updateLocalUserPref(userPref: LocalUserPref): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: UPDATE_LOCAL_USER_PREF,
      payload: userPref,
    });
  };
}

export function disableAllTips(): ReduxAction<void> {
  return (dispatch) => {
    dispatch(updateUserParams({ showTips: false }));
    dispatch({
      type: UPDATE_TIPS_ARE_OFF,
      payload: true,
    });
  };
}

export function hideTipsAreOff() {
  return {
    type: UPDATE_TIPS_ARE_OFF,
    payload: false,
  };
}

// the user can enable the intro again but that new state will only be stored on the local workstation state, valid only for current session
export function showIntroLocally() {
  return {
    type: UPDATE_SHOW_INTRO,
    payload: true,
  };
}

export function showGettingStartedTips(showTips: boolean): ReduxAction<void> {
  return (dispatch) => dispatch(updateUserParams({ showTips: showTips }));
}

export function completeIntroduction(): ReduxAction<void> {
  return (dispatch) => {
    pushRoute("/content");
    dispatch(
      updateUserParams({
        firstLogin: false,
        showTips: true,
      }),
    );
  };
}

export function interruptIntroduction(): ReduxAction<void> {
  return (dispatch) => dispatch(updateUserParams({ firstLogin: false }));
}

export function auditRouteChange(previousRoute: Location, nextRoute: Location): ReduxAction<void> {
  return (_dispatch, _getState, webApiClient) => {
    if (nextRoute.pathname === "/inprogress") {
      return;
    }
    const srcURL = previousRoute ? `${previousRoute.pathname}${previousRoute.search}` : "";
    const destURL = `${nextRoute.pathname}${nextRoute.search}`;
    if (srcURL === destURL) {
      // prevent logging when changing to the current path again.
      return;
    }
    let logMessage = "Navigated ";
    logMessage += previousRoute ? "from " + srcURL + " " : "";
    logMessage += "to " + destURL;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.ROUTE_CHANGE,
      message: logMessage,
      payload: { destURL },
    };
    webApiClient.auditLog(log);
  };
}

function auditUserLogin(): ReduxAction<void> {
  return (_dispatch, getState, webApiClient) => {
    const state: IAppState = getState();
    const logMessage: string = 'Login of "' + state.auth.session.username + '"';
    const payload = null;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.AUTHENTICATION,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

export function updateReferenceGenome(refGenomeId: number): ReduxAction<void> {
  return (dispatch) => {
    // the 2 dispatched actions should be in the following order, do NOT change (related to issue of http://jira-sel.ch.genedata.com/browse/SEL6-2008?focusedCommentId=28440&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-28440)
    // otherwise the RefGenomeSelector component's lifecycle method componentDidUpdate will catch the intermediate change and interpret it incorrectly
    dispatch(updateUserParams({ referenceGenome: refGenomeId }));
    dispatch({
      type: SET_REFERENCE_GENOME,
    });
  };
}

export function getRefGenomeFromSavedView(view: View): number {
  let viewRefGenomeId: number = null;
  switch (view.viewType) {
    case VIEW.TABLE_VIEW.type: {
      viewRefGenomeId =
        view.state.tableView.tableQuerySpec.restrictions &&
        view.state.tableView.tableQuerySpec.restrictions.refGenomeId;

      break;
    }
    case VIEW.ENTITY_SUMMARY_VIEW.type: {
      viewRefGenomeId = view.state.summaryView.refGenomeId;

      break;
    }
    case VIEW.GENOME_VIEW.type: {
      viewRefGenomeId = view.state.genomeView.refGenome && view.state.genomeView.refGenome.id;

      break;
    }
    // No default
  }
  return viewRefGenomeId;
}
